import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  BarcodeFormat,
  BrowserMultiFormatReader,
} from "@zxing/browser/umd/zxing-browser.min.js";
import { createWorker, PSM } from "tesseract.js";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LogoutIcon from "@mui/icons-material/Logout";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SaveIcon from "@mui/icons-material/Save";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import Helpers from "../Config/Helpers";
import { useVinDecoder } from "../hooks/useVinDecoder";
import { extractVinFromBarcodeText, extractVinFromOcrText } from "../utils/vinOcr";
import {
  buildCustomerPayload,
  createCustomerInitialValues,
  formatCustomerPhone,
} from "./Customers/customerFormConfig";
import {
  buildVehiclePayload,
  createVehicleInitialValues,
  mapVinDecodedVehicle,
  prepareVehicleFormValues,
} from "./Vehicles/vehicleFormConfig";
import "./CheckIn.css";

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{11,17}$/;
const VIN_OCR_CHAR_WHITELIST = "ABCDEFGHJKLMNPRSTUVWXYZ0123456789IOQ";
const SCANNER_TIMEOUT_MS = 12000;
const VIN_BARCODE_FORMATS = [
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.PDF_417,
  BarcodeFormat.QR_CODE,
];
const NATIVE_VIN_BARCODE_FORMATS = [
  "code_39",
  "code_93",
  "code_128",
  "data_matrix",
  "pdf417",
  "qr_code",
];

const reasonOptions = [
  { value: "Inspection", label: "Inspection" },
  { value: "Oil Change", label: "Oil Change" },
  { value: "Brakes", label: "Brakes" },
  { value: "Tires", label: "Tires" },
  { value: "Other", label: "Other" },
];

const emptyVehicle = (customer = "") => ({
  ...createVehicleInitialValues(Helpers.authParentId),
  customer,
});

const authHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
    "x-access-token": localStorage.getItem("token"),
  },
});

const normalizeVin = (value = "") => {
  const compact = String(value || "")
    .toUpperCase()
    .replace(/[^A-HJ-NPR-Z0-9]/g, "");
  const match = compact.match(/[A-HJ-NPR-Z0-9]{17}/);
  return match ? match[0] : compact.slice(0, 17);
};

const loadImageFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image."));
    };
    image.src = url;
  });

const createVinOcrCanvas = async (file) => {
  const image = await loadImageFile(file);
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
  const scale =
    longestSide > 2200
      ? 2200 / longestSide
      : Math.min(2, Math.max(1, 1200 / Math.max(longestSide, 1)));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });

  canvas.width = width;
  canvas.height = height;
  if (!context) throw new Error("Could not prepare image.");
  context.imageSmoothingEnabled = true;
  context.drawImage(image, 0, 0, width, height);

  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  for (let index = 0; index < data.length; index += 4) {
    const gray = data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
    let value = (gray - 128) * 1.45 + 128;
    if (value > 205) value = 255;
    if (value < 50) value = 0;
    data[index] = value;
    data[index + 1] = value;
    data[index + 2] = value;
  }
  context.putImageData(imageData, 0, 0);

  return canvas;
};

const formatVehicleLabel = (vehicle = {}) =>
  [vehicle.year, vehicle.make || vehicle.name, vehicle.model]
    .filter(Boolean)
    .join(" ") || "Vehicle";

const resolveCustomerId = (customer) => String(customer?._id || customer || "");

function CheckIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const sourceAppointment = location.state?.appointment || null;
  const isCheckInOnly = Helpers.isCheckInOnlyUser();
  const handleLogout = () => {
    localStorage.clear();
    Helpers.toast("success", "Logged out successfully");
    navigate("/");
  };
  const shop = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("shop")) || {};
    } catch (_error) {
      return {};
    }
  }, []);

  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerMode, setCustomerMode] = useState("search");
  const [customerForm, setCustomerForm] = useState(
    createCustomerInitialValues(Helpers.authParentId),
  );
  const [customerSaving, setCustomerSaving] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleResults, setVehicleResults] = useState([]);
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle());
  const [vehicleSaving, setVehicleSaving] = useState(false);

  const [miles, setMiles] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReason, setOtherReason] = useState("");
  const [notes, setNotes] = useState("");
  const [savingQuote, setSavingQuote] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);
  const [formError, setFormError] = useState("");

  const [scannerActive, setScannerActive] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [vinPhotoLoading, setVinPhotoLoading] = useState(false);
  const [vinPhotoProgress, setVinPhotoProgress] = useState(0);
  const videoRef = useRef(null);
  const vinPhotoInputRef = useRef(null);
  const appointmentLoadedRef = useRef(false);
  const streamRef = useRef(null);
  const scanFrameRef = useRef(null);
  const scannerTimeoutRef = useRef(null);
  const detectorRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const ocrWorkerRef = useRef(null);

  const handleVinDecoded = useCallback((mapped, _onlyFillEmpty, sourceVin) => {
    setVehicleForm((current) => ({
      ...current,
      ...mapVinDecodedVehicle(mapped, current, false),
      vin_number: sourceVin || current.vin_number,
      no_vin: false,
    }));
  }, []);

  const { vinLoading, vinError, onVinChange, onVinBlur, fetchVin } =
    useVinDecoder(handleVinDecoded, {
      baseUrl: Helpers.baseUrl,
      axiosConfig: authHeaders(),
      endpointPrefix: "vin/",
      debounceMs: 450,
    });

  const clearScannerTimeout = useCallback(() => {
    if (scannerTimeoutRef.current) {
      clearTimeout(scannerTimeoutRef.current);
      scannerTimeoutRef.current = null;
    }
  }, []);

  const stopScanner = useCallback(() => {
    clearScannerTimeout();
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }
    if (zxingControlsRef.current) {
      const controls = zxingControlsRef.current;
      zxingControlsRef.current = null;
      try {
        const stopResult = controls.stop();
        if (stopResult?.catch) stopResult.catch(() => {});
      } catch (_error) {
        // ZXing may already have stopped the stream after a successful scan.
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScannerActive(false);
  }, [clearScannerTimeout]);

  const startScannerTimeout = useCallback(() => {
    clearScannerTimeout();
    scannerTimeoutRef.current = setTimeout(() => {
      setScannerError("Could not read the barcode. Try a closer photo of the VIN text or type it.");
      stopScanner();
    }, SCANNER_TIMEOUT_MS);
  }, [clearScannerTimeout, stopScanner]);

  const terminateOcrWorker = useCallback(() => {
    if (!ocrWorkerRef.current) return;
    const worker = ocrWorkerRef.current;
    ocrWorkerRef.current = null;
    worker.terminate().catch(() => {});
  }, []);

  const getOcrWorker = useCallback(async () => {
    if (ocrWorkerRef.current) return ocrWorkerRef.current;

    const worker = await createWorker("eng", 1, {
      logger: (message) => {
        if (typeof message?.progress === "number") {
          setVinPhotoProgress(Math.max(1, Math.round(message.progress * 100)));
        }
      },
    });
    await worker.setParameters({
      tessedit_char_whitelist: VIN_OCR_CHAR_WHITELIST,
      tessedit_pageseg_mode: PSM.SPARSE_TEXT,
      preserve_interword_spaces: "0",
      user_defined_dpi: "300",
    });
    ocrWorkerRef.current = worker;
    return worker;
  }, []);

  const loadCustomerVehicles = useCallback(
    async (customerId) => {
      if (!customerId) {
        setCustomerVehicles([]);
        return [];
      }
      setVehicleLoading(true);
      try {
        const response = await axios.get(
          `${Helpers.baseUrl}/vehicles/customer/${customerId}`,
          authHeaders(),
        );
        const data = Array.isArray(response.data) ? response.data : [];
        setCustomerVehicles(data);
        return data;
      } catch (error) {
        Helpers.unauthenticated(error, navigate);
        return [];
      } finally {
        setVehicleLoading(false);
      }
    },
    [navigate],
  );

  const selectVehicle = useCallback(
    async (vehicle) => {
      if (!vehicle) return;

      setSelectedVehicle(vehicle);
      setVehicleForm(prepareVehicleFormValues(vehicle, Helpers.authParentId));
      setVehicleSearch("");
      setVehicleResults([]);

      const linkedCustomer =
        vehicle.customer && typeof vehicle.customer === "object"
          ? vehicle.customer
          : null;

      if (linkedCustomer?._id) {
        setSelectedCustomer(linkedCustomer);
        setCustomerQuery("");
        setCustomerResults([]);
        setCustomerMode("search");
        await loadCustomerVehicles(linkedCustomer._id);
      }

      try {
        const response = await axios.get(
          `${Helpers.baseUrl}/vehicles/${vehicle._id}/last-odometer`,
          authHeaders(),
        );
        if (response.data?.ok && response.data.currentMiles) {
          setMiles(response.data.currentMiles);
        }
      } catch (_error) {
        if (vehicle.miles) setMiles(vehicle.miles);
      }
    },
    [loadCustomerVehicles],
  );

  const searchVehiclesByVin = useCallback(
    async (vin, autoSelectExact = false) => {
      const query = normalizeVin(vin);
      if (query.length < 6) {
        setVehicleResults([]);
        return [];
      }

      try {
        const response = await axios.get(`${Helpers.baseUrl}/vehicles/search`, {
          ...authHeaders(),
          params: { q: query },
        });
        const data = Array.isArray(response.data) ? response.data : [];
        setVehicleResults(data);

        const exact = data.find(
          (vehicle) =>
            normalizeVin(vehicle.vin_number) &&
            normalizeVin(vehicle.vin_number) === query,
        );
        const suffixMatches = data.filter((vehicle) => {
          const vehicleVin = normalizeVin(vehicle.vin_number);
          return vehicleVin && vehicleVin.endsWith(query);
        });
        const match = exact || (suffixMatches.length === 1 ? suffixMatches[0] : null);

        if (autoSelectExact && match) {
          await selectVehicle(match);
        }
        return data;
      } catch (error) {
        Helpers.unauthenticated(error, navigate);
        return [];
      }
    },
    [navigate, selectVehicle],
  );

  const applyVinValue = useCallback(
    async (value, decodeNow = false, autoSelectExact = false) => {
      const vin = normalizeVin(value);
      setVehicleForm((current) => ({
        ...current,
        vin_number: vin,
        no_vin: false,
      }));
      setVehicleSearch(vin);
      setSelectedVehicle(null);

      if (VIN_REGEX.test(vin)) {
        if (decodeNow) {
          await fetchVin(vin);
        } else {
          onVinChange(vin);
        }
      }
      if (vin.length >= 6) {
        setVehicleSearchLoading(true);
        try {
          await searchVehiclesByVin(vin, autoSelectExact);
        } finally {
          setVehicleSearchLoading(false);
        }
      } else {
        setVehicleResults([]);
      }
    },
    [fetchVin, onVinChange, searchVehiclesByVin],
  );

  const handleVinLookupChange = (value) => {
    const vin = normalizeVin(value);
    setVehicleSearch(vin);
    setVehicleForm((current) => ({
      ...current,
      vin_number: vin,
      no_vin: false,
    }));
    setSelectedVehicle(null);
    setSavedQuote(null);

    if (VIN_REGEX.test(vin)) {
      onVinChange(vin);
    }
  };

  const handleVinLookupBlur = (value) => {
    const vin = normalizeVin(value);
    setVehicleSearch(vin);
    setVehicleForm((current) => ({
      ...current,
      vin_number: vin,
    }));

    if (VIN_REGEX.test(vin)) {
      onVinBlur(vin);
    }
  };

  useEffect(
    () => () => {
      stopScanner();
      terminateOcrWorker();
    },
    [stopScanner, terminateOcrWorker],
  );

  useEffect(() => {
    const query = customerQuery.trim();
    if (query.length < 2 || selectedCustomer) {
      setCustomerResults([]);
      setCustomerLoading(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setCustomerLoading(true);
      axios
        .get(`${Helpers.baseUrl}/customers/search`, {
          ...authHeaders(),
          params: { q: query },
        })
        .then((response) => {
          setCustomerResults(Array.isArray(response.data) ? response.data : []);
        })
        .catch((error) => Helpers.unauthenticated(error, navigate))
        .finally(() => setCustomerLoading(false));
    }, 250);

    return () => clearTimeout(timeout);
  }, [customerQuery, navigate, selectedCustomer]);

  useEffect(() => {
    const query = normalizeVin(vehicleSearch);
    if (selectedVehicle || query.length < 6) {
      setVehicleResults([]);
      setVehicleSearchLoading(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setVehicleSearchLoading(true);
      searchVehiclesByVin(query, true)
        .finally(() => setVehicleSearchLoading(false));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchVehiclesByVin, selectedVehicle, vehicleSearch]);

  useEffect(() => {
    if (!sourceAppointment || appointmentLoadedRef.current) return;
    appointmentLoadedRef.current = true;

    const preloadAppointment = async () => {
      const appointmentCustomer = sourceAppointment.customer || null;
      const appointmentVehicle = sourceAppointment.vehicle || null;

      if (appointmentCustomer?._id) {
        setSelectedCustomer(appointmentCustomer);
        setCustomerQuery("");
        setCustomerResults([]);
        setCustomerMode("search");
        setVehicleForm(emptyVehicle(appointmentCustomer._id));
        await loadCustomerVehicles(appointmentCustomer._id);
      }

      if (appointmentVehicle?._id) {
        await selectVehicle(appointmentVehicle);
      }

      const reason = sourceAppointment.reason || "";
      if (reason) {
        const knownReason = reasonOptions.some((option) => option.value === reason);
        setSelectedReasons([knownReason ? reason : "Other"]);
        setOtherReason(
          reason === "Other"
            ? sourceAppointment.otherReason || ""
            : knownReason
              ? sourceAppointment.otherReason || ""
              : reason,
        );
      }

      setNotes(sourceAppointment.notes || "");
      setSavedQuote(null);
      setFormError("");
    };

    preloadAppointment();
  }, [loadCustomerVehicles, selectVehicle, sourceAppointment]);

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery("");
    setCustomerResults([]);
    setCustomerMode("search");
    setVehicleForm((current) => {
      const hasVehicleDraft = Boolean(
        current.vin_number ||
          current.year ||
          current.make ||
          current.model ||
          current.engine_size ||
          current.engine_cylinders ||
          current.no_vin,
      );

      return hasVehicleDraft
        ? { ...current, customer: customer?._id || "" }
        : emptyVehicle(customer?._id || "");
    });
    setVehicleResults([]);
    setSavedQuote(null);
    await loadCustomerVehicles(customer?._id);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerVehicles([]);
    setCustomerQuery("");
    setCustomerResults([]);
    setCustomerMode("search");
    setVehicleForm((current) =>
      selectedVehicle ? { ...current, customer: "" } : emptyVehicle(),
    );
    if (!selectedVehicle) {
      setVehicleSearch("");
      setVehicleResults([]);
      setMiles("");
    }
    setSavedQuote(null);
  };

  const saveCustomer = async () => {
    setFormError("");
    const payload = buildCustomerPayload(customerForm, Helpers.authParentId);
    if (!payload.name || !payload.phone) {
      setFormError("Enter the customer's name and phone number.");
      return;
    }

    setCustomerSaving(true);
    try {
      const response = await axios.post(
        `${Helpers.baseUrl}/customers/add`,
        payload,
        authHeaders(),
      );
      Helpers.toast("success", "Customer saved");
      setCustomerForm(createCustomerInitialValues(Helpers.authParentId));
      await selectCustomer(response.data);
    } catch (error) {
      setFormError("Could not save customer. Check the required fields.");
      Helpers.error_response(error);
      Helpers.unauthenticated(error, navigate);
    } finally {
      setCustomerSaving(false);
    }
  };

  const createVehicle = async () => {
    if (!selectedCustomer) {
      setFormError("Select or create a customer before adding a vehicle.");
      return null;
    }

    const prepared = prepareVehicleFormValues(vehicleForm, Helpers.authParentId);
    const hasVin = VIN_REGEX.test(normalizeVin(prepared.vin_number));
    if (!prepared.no_vin && !hasVin) {
      setFormError("Enter or scan a valid VIN, or mark the vehicle as No VIN.");
      return null;
    }
    if (!prepared.year || !prepared.make || !prepared.model) {
      setFormError("Year, make, and model are required for a new vehicle.");
      return null;
    }

    setVehicleSaving(true);
    try {
      const payload = buildVehiclePayload(
        {
          ...prepared,
          customer: selectedCustomer,
          vin_number: normalizeVin(prepared.vin_number),
        },
        [selectedCustomer],
        Helpers.authParentId,
      );
      const response = await axios.post(
        `${Helpers.baseUrl}/vehicles/add`,
        payload,
        authHeaders(),
      );
      Helpers.toast("success", "Vehicle saved");
      setCustomerVehicles((current) => [response.data, ...current]);
      await selectVehicle(response.data);
      return response.data;
    } catch (error) {
      setFormError("Could not save vehicle. Check the vehicle details.");
      Helpers.error_response(error);
      Helpers.unauthenticated(error, navigate);
      return null;
    } finally {
      setVehicleSaving(false);
    }
  };

  const syncSelectedVehicleCustomer = async (vehicle) => {
    const selectedCustomerId = resolveCustomerId(selectedCustomer);
    const vehicleCustomerId = resolveCustomerId(vehicle?.customer);

    if (!vehicle?._id || !selectedCustomerId || vehicleCustomerId === selectedCustomerId) {
      return vehicle;
    }

    const prepared = prepareVehicleFormValues(
      {
        ...vehicle,
        ...vehicleForm,
        _id: vehicle._id,
        customer: selectedCustomer,
      },
      Helpers.authParentId,
    );
    const payload = buildVehiclePayload(
      {
        ...prepared,
        _id: vehicle._id,
        customer: selectedCustomer,
        vin_number: normalizeVin(prepared.vin_number),
      },
      [selectedCustomer],
      Helpers.authParentId,
    );

    try {
      await axios.post(
        `${Helpers.baseUrl}/vehicles/update`,
        { ...payload, _id: vehicle._id },
        authHeaders(),
      );

      const updatedVehicle = {
        ...vehicle,
        ...payload,
        _id: vehicle._id,
        customer: selectedCustomer,
      };
      setSelectedVehicle(updatedVehicle);
      setVehicleForm(prepareVehicleFormValues(updatedVehicle, Helpers.authParentId));
      setCustomerVehicles((current) => {
        const exists = current.some(
          (item) => String(item?._id || "") === String(updatedVehicle._id),
        );
        return exists
          ? current.map((item) =>
              String(item?._id || "") === String(updatedVehicle._id)
                ? updatedVehicle
                : item,
            )
          : [updatedVehicle, ...current];
      });
      return updatedVehicle;
    } catch (error) {
      setFormError("Could not update the vehicle owner. Try again.");
      Helpers.error_response(error);
      Helpers.unauthenticated(error, navigate);
      return null;
    }
  };

  const ensureVehicle = async () => {
    if (selectedVehicle) return syncSelectedVehicleCustomer(selectedVehicle);
    return createVehicle();
  };

  const toggleReason = (reason) => {
    setSelectedReasons((current) =>
      current.includes(reason)
        ? current.filter((value) => value !== reason)
        : [...current, reason],
    );
  };

  const buildTechNotes = () => {
    const reasonText = selectedReasons.length
      ? selectedReasons
          .map((reason) =>
            reason === "Other" && otherReason.trim()
              ? `Other: ${otherReason.trim()}`
              : reason,
          )
          .join(", ")
      : "Not specified";
    return [
      `Check-in reason: ${reasonText}`,
      notes.trim() ? `Customer concern: ${notes.trim()}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  };

  const saveQuote = async () => {
    setFormError("");
    if (!selectedCustomer) {
      setFormError("Find a vehicle by VIN or select/create a customer first.");
      return;
    }

    const vehicle = await ensureVehicle();
    if (!vehicle) return;

    const numericMiles = Number(miles);
    const payload = {
      customer: selectedCustomer,
      vehicle,
      miles: Number.isFinite(numericMiles) && numericMiles > 0 ? numericMiles : 0,
      services: [],
      tires: {},
      tiresPrice: 0,
      tiresQuantity: 0,
      oil: null,
      oilQuantity: 0,
      oilPrice: 0,
      extraOilSelection: null,
      extraOilPrice: 0,
      extraOilQuantity: 0,
      fullServiceIncluded: false,
      fullServicePrice: 0,
      oilFilter: null,
      oilFilterQuantity: 0,
      oilFilterPrice: 0,
      taxIncluded: selectedCustomer.tax === "Taxable",
      taxType: 0,
      tax: 0,
      discountIncluded: false,
      discountType: "",
      discount: 0,
      totalPrice: 0,
      status: "Unpaid",
      receiptType: "Quote",
      paymentType: null,
      paid: 0,
      remaining: 0,
      date: new Date().toISOString(),
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      techNotes: buildTechNotes(),
      checkInReasons: selectedReasons,
      checkInOther: otherReason.trim(),
    };

    setSavingQuote(true);
    try {
      const response = await axios.post(
        `${Helpers.baseUrl}/receipts/add`,
        payload,
        authHeaders(),
      );

      if (vehicle?._id && payload.miles > 0) {
        try {
          await axios.patch(
            `${Helpers.baseUrl}/vehicles/update-odometer/${vehicle._id}`,
            { miles: payload.miles, unit: "mi", receiptId: response.data._id },
            authHeaders(),
          );
        } catch (error) {
          console.warn("Odometer update failed:", error?.response?.data || error);
        }
      }

      if (sourceAppointment?._id) {
        try {
          await axios.patch(
            `${Helpers.baseUrl}/appointments/status/${sourceAppointment._id}`,
            { status: "Checked In" },
            authHeaders(),
          );
        } catch (error) {
          console.warn("Appointment status update failed:", error?.response?.data || error);
        }
      }

      setSavedQuote(response.data);
      Helpers.toast("success", "Check-in quote saved");
    } catch (error) {
      setFormError("Could not save the quote. Try again.");
      Helpers.error_response(error);
      Helpers.unauthenticated(error, navigate);
    } finally {
      setSavingQuote(false);
    }
  };

  const startNativeScanner = async () => {
    let formats = NATIVE_VIN_BARCODE_FORMATS;
    if (window.BarcodeDetector.getSupportedFormats) {
      const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
      formats = NATIVE_VIN_BARCODE_FORMATS.filter((format) =>
        supportedFormats.includes(format),
      );
      if (!formats.length) {
        throw new Error("No supported VIN barcode formats.");
      }
    }

    detectorRef.current = new window.BarcodeDetector({
      formats,
    });
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    }
    setScannerActive(true);

    const scan = async () => {
      if (!videoRef.current || !detectorRef.current) return;
      try {
        const barcodes = await detectorRef.current.detect(videoRef.current);
        const vin = (barcodes || [])
          .map((barcode) => extractVinFromBarcodeText(barcode?.rawValue || ""))
          .find(Boolean);

        if (vin) {
          setScannerError("");
          await applyVinValue(vin, true, true);
          stopScanner();
          return;
        }
        if (barcodes?.length) {
          setScannerError("Scanner found a barcode, but it was not a valid VIN.");
        }
      } catch (_error) {
        // Keep scanning. Some browsers throw while the video is warming up.
      }
      scanFrameRef.current = requestAnimationFrame(scan);
    };

    scanFrameRef.current = requestAnimationFrame(scan);
  };

  const startZxingScanner = async () => {
    const reader = new BrowserMultiFormatReader(undefined, {
      delayBetweenScanAttempts: 180,
      delayBetweenScanSuccess: 500,
      tryPlayVideoTimeout: 7000,
    });
    reader.possibleFormats = VIN_BARCODE_FORMATS;

    const controls = await reader.decodeFromConstraints(
      {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      },
      videoRef.current,
      async (result, _error, scannerControls) => {
        if (!result) return;

        const vin = extractVinFromBarcodeText(result.getText());
        if (!vin) {
          setScannerError("Scanner found a barcode, but it was not a valid VIN.");
          return;
        }

        try {
          setScannerError("");
          const stopResult = scannerControls?.stop();
          if (stopResult?.catch) stopResult.catch(() => {});
        } catch (_stopError) {
          // The main stop routine below will clean up any remaining stream.
        }
        await applyVinValue(vin, true, true);
        stopScanner();
      },
    );
    zxingControlsRef.current = controls;
    setScannerActive(true);
  };

  const startScanner = async () => {
    setScannerError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setScannerError("Camera access is not available in this browser.");
      return;
    }
    if (!videoRef.current) {
      setScannerError("Camera preview is not ready yet.");
      return;
    }

    try {
      await startZxingScanner();
      startScannerTimeout();
    } catch (_zxingError) {
      if (window.BarcodeDetector) {
        try {
          await startNativeScanner();
          startScannerTimeout();
          return;
        } catch (_nativeError) {
          stopScanner();
        }
      }
      setScannerError("Camera permission was blocked or unavailable.");
      stopScanner();
    }
  };

  const handleVinPhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    stopScanner();
    setScannerError("");
    setVinPhotoLoading(true);
    setVinPhotoProgress(1);

    try {
      const canvas = await createVinOcrCanvas(file);
      const worker = await getOcrWorker();
      const { data } = await worker.recognize(
        canvas,
        { rotateAuto: true },
        { text: true },
      );
      const vin = extractVinFromOcrText(data?.text || "");

      if (!vin) {
        setScannerError("Could not read a VIN from that photo. Try a closer, clearer photo or type it.");
        return;
      }

      setVinPhotoProgress(100);
      await applyVinValue(vin, true, true);
      Helpers.toast("success", "VIN read from photo");
    } catch (_error) {
      setScannerError("Could not read the VIN photo. Try again or type the VIN.");
      terminateOcrWorker();
    } finally {
      setVinPhotoLoading(false);
      setVinPhotoProgress(0);
    }
  };

  const resetCheckIn = () => {
    setSelectedVehicle(null);
    setVehicleForm(emptyVehicle(selectedCustomer?._id || ""));
    setVehicleSearch("");
    setVehicleResults([]);
    setMiles("");
    setSelectedReasons([]);
    setOtherReason("");
    setNotes("");
    setSavedQuote(null);
    setFormError("");
  };

  const vinLookupLength = normalizeVin(vehicleSearch).length;
  const showCustomerVehicleList = Boolean(selectedCustomer) && vinLookupLength < 6;
  const showVehicleLookupEmpty =
    vinLookupLength >= 6 &&
    !vehicleSearchLoading &&
    vehicleResults.length === 0 &&
    !selectedVehicle;
  const selectedCustomerId = resolveCustomerId(selectedCustomer);
  const selectedVehicleCustomerId = resolveCustomerId(selectedVehicle?.customer);
  const vehicleOwnerWillChange = Boolean(
    selectedVehicle?._id &&
      selectedCustomerId &&
      selectedVehicleCustomerId !== selectedCustomerId,
  );
  const canSave = Boolean(selectedCustomer) && !savingQuote && !vehicleSaving;

  return (
    <main className="checkin-page">
      <header className="checkin-header">
        {!isCheckInOnly ? (
          <button
            type="button"
            className="checkin-icon-button"
            onClick={() => navigate("/user/dashboard")}
            aria-label="Back to dashboard"
          >
            <ArrowBackIcon fontSize="small" />
          </button>
        ) : null}
        <div className="checkin-header-title">
          <p>{shop?.name || "Shop"}</p>
          <h1>Check-in</h1>
        </div>
        <button
          type="button"
          className="checkin-icon-button"
          onClick={handleLogout}
          aria-label="Logout"
          title="Logout"
        >
          <LogoutIcon fontSize="small" />
        </button>
      </header>

      {formError ? <div className="checkin-alert">{formError}</div> : null}

      {savedQuote ? (
        <section className="checkin-success" aria-live="polite">
          <CheckCircleIcon />
          <div>
            <h2>Quote saved</h2>
            <p>
              {savedQuote.friendlyNumber
                ? `Estimate #${savedQuote.friendlyNumber}`
                : "The check-in is saved as an estimate."}
            </p>
          </div>
          <div className="checkin-success-actions">
            {!isCheckInOnly ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/user/receipts/receipt-details/${savedQuote._id}`)
                  }
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate("/user/receipts/add", {
                      state: { receiptToEdit: savedQuote },
                    })
                  }
                >
                  Edit RO
                </button>
              </>
            ) : null}
            <button type="button" onClick={resetCheckIn}>
              {isCheckInOnly ? "Start another check-in" : "New"}
            </button>
          </div>
        </section>
      ) : null}

      <section className="checkin-section">
        <div className="checkin-section-title">
          <DirectionsCarIcon />
          <h2>Vehicle</h2>
        </div>

        {selectedVehicle ? (
          <div className="checkin-selected">
            <div>
              <strong>{formatVehicleLabel(selectedVehicle)}</strong>
              <span>{selectedVehicle.vin_number || "No VIN"}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedVehicle(null);
                setVehicleForm(emptyVehicle(selectedCustomer?._id || ""));
                setVehicleSearch("");
                setVehicleResults([]);
                setMiles("");
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <div className="checkin-vin-row">
              <label className="checkin-field">
                <span>VIN lookup</span>
                <input
                  type="search"
                  value={vehicleSearch}
                  onChange={(event) => handleVinLookupChange(event.target.value)}
                  onBlur={(event) => handleVinLookupBlur(event.target.value)}
                  placeholder="Last 6 or full VIN"
                  autoCapitalize="characters"
                  autoComplete="off"
                />
              </label>
              <input
                ref={vinPhotoInputRef}
                className="checkin-file-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleVinPhotoChange}
              />
              <button
                type="button"
                className="checkin-camera-button"
                onClick={() => vinPhotoInputRef.current?.click()}
                aria-label="Read VIN from photo"
                disabled={vinPhotoLoading}
              >
                <PhotoCameraIcon />
                <span>{vinPhotoLoading ? "Reading" : "Photo"}</span>
              </button>
              <button
                type="button"
                className="checkin-camera-button"
                onClick={scannerActive ? stopScanner : startScanner}
                aria-label={scannerActive ? "Stop VIN scanner" : "Scan VIN"}
                disabled={vinPhotoLoading}
              >
                {scannerActive ? <StopCircleIcon /> : <QrCodeScannerIcon />}
                <span>{scannerActive ? "Stop" : "Barcode"}</span>
              </button>
            </div>

            {vinLoading ? <p className="checkin-muted">Decoding VIN...</p> : null}
            {vinPhotoLoading ? (
              <p className="checkin-muted">
                Reading VIN from photo{vinPhotoProgress ? ` ${vinPhotoProgress}%` : ""}
              </p>
            ) : null}
            {vinError ? <p className="checkin-error">{vinError}</p> : null}
            {scannerError ? <p className="checkin-error">{scannerError}</p> : null}

            <div className={`checkin-scanner ${scannerActive ? "active" : ""}`}>
              <video ref={videoRef} muted playsInline />
              <div className="checkin-scan-frame" />
            </div>

            <div className="checkin-result-list">
              {vehicleSearchLoading ? <p>Searching vehicles...</p> : null}
              {!vehicleSearchLoading &&
                vehicleResults.map((vehicle) => (
                  <button
                    type="button"
                    key={vehicle._id}
                    onClick={() => selectVehicle(vehicle)}
                  >
                    <strong>{formatVehicleLabel(vehicle)}</strong>
                    <span>
                      {[vehicle.vin_number || "No VIN", vehicle.customer?.name]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                ))}
              {showVehicleLookupEmpty ? <p>No saved vehicle found for that VIN</p> : null}
            </div>

            {showCustomerVehicleList ? (
              <div className="checkin-vehicle-list">
                {vehicleLoading ? <p>Loading vehicles...</p> : null}
                {!vehicleLoading &&
                  customerVehicles.map((vehicle) => (
                    <button
                      type="button"
                      key={vehicle._id}
                      onClick={() => selectVehicle(vehicle)}
                    >
                      <strong>{formatVehicleLabel(vehicle)}</strong>
                      <span>{vehicle.vin_number || "No VIN"}</span>
                    </button>
                  ))}
                {!vehicleLoading && customerVehicles.length === 0 ? (
                  <p>No vehicles saved for this customer</p>
                ) : null}
              </div>
            ) : null}

            {selectedCustomer ? (
              <>
                <label className="checkin-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(vehicleForm.no_vin)}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      if (checked) {
                        setVehicleSearch("");
                        setVehicleResults([]);
                      }
                      setVehicleForm((current) => ({
                        ...current,
                        no_vin: checked,
                        vin_number: checked ? "" : current.vin_number,
                      }));
                    }}
                  />
                  <span>No VIN available</span>
                </label>

                <div className="checkin-grid">
                  <label className="checkin-field">
                    <span>Year</span>
                    <input
                      value={vehicleForm.year || ""}
                      onChange={(event) =>
                        setVehicleForm((current) => ({
                          ...current,
                          year: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="checkin-field">
                    <span>Make</span>
                    <input
                      value={vehicleForm.make || ""}
                      onChange={(event) =>
                        setVehicleForm((current) => ({
                          ...current,
                          make: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="checkin-field">
                    <span>Model</span>
                    <input
                      value={vehicleForm.model || ""}
                      onChange={(event) =>
                        setVehicleForm((current) => ({
                          ...current,
                          model: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="checkin-field">
                    <span>Engine</span>
                    <input
                      value={vehicleForm.engine_size || ""}
                      onChange={(event) =>
                        setVehicleForm((current) => ({
                          ...current,
                          engine_size: event.target.value,
                        }))
                      }
                      placeholder="Optional"
                    />
                  </label>
                  <label className="checkin-field">
                    <span>Drivetrain</span>
                    <select
                      value={vehicleForm.drivetrain || ""}
                      onChange={(event) =>
                        setVehicleForm((current) => ({
                          ...current,
                          drivetrain: event.target.value,
                        }))
                      }
                    >
                      <option value="">Optional</option>
                      <option value="2WD">2WD</option>
                      <option value="4WD">4WD</option>
                      <option value="AWD">AWD</option>
                      <option value="FWD">FWD</option>
                      <option value="RWD">RWD</option>
                    </select>
                  </label>
                </div>

                <button
                  type="button"
                  className="checkin-action secondary"
                  onClick={createVehicle}
                  disabled={vehicleSaving}
                >
                  <AddIcon fontSize="small" />
                  {vehicleSaving ? "Saving..." : "Add Vehicle"}
                </button>
              </>
            ) : (
              <p className="checkin-muted">
                If this is a new vehicle, select or create the customer below before adding it.
              </p>
            )}
          </>
        )}
      </section>

      <section className="checkin-section">
        <div className="checkin-section-title">
          <PersonSearchIcon />
          <h2>Customer</h2>
        </div>

        {selectedCustomer ? (
          <div className="checkin-selected">
            <div>
              <strong>{selectedCustomer.name}</strong>
              <span>{formatCustomerPhone(selectedCustomer.phone)}</span>
              {vehicleOwnerWillChange ? (
                <span>Vehicle owner will update on save</span>
              ) : null}
            </div>
            <button type="button" onClick={clearCustomer}>
              {selectedVehicle ? "Change Owner" : "Change"}
            </button>
          </div>
        ) : (
          <>
            <div className="checkin-segment">
              <button
                type="button"
                className={customerMode === "search" ? "active" : ""}
                onClick={() => setCustomerMode("search")}
              >
                Search
              </button>
              <button
                type="button"
                className={customerMode === "new" ? "active" : ""}
                onClick={() => setCustomerMode("new")}
              >
                New
              </button>
            </div>

            {customerMode === "search" ? (
              <>
                <label className="checkin-field">
                  <span>Name or phone</span>
                  <input
                    type="search"
                    value={customerQuery}
                    onChange={(event) => setCustomerQuery(event.target.value)}
                    placeholder="Start typing..."
                    autoComplete="off"
                  />
                </label>
                <div className="checkin-result-list">
                  {customerLoading ? <p>Searching...</p> : null}
                  {!customerLoading &&
                    customerResults.map((customer) => (
                      <button
                        type="button"
                        key={customer._id}
                        onClick={() => selectCustomer(customer)}
                      >
                        <strong>{customer.name}</strong>
                        <span>
                          {[formatCustomerPhone(customer.phone), customer.email]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    ))}
                  {!customerLoading &&
                  customerQuery.trim().length >= 2 &&
                  customerResults.length === 0 ? (
                    <p>No matching customers</p>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="checkin-grid">
                <label className="checkin-field">
                  <span>First name</span>
                  <input
                    value={customerForm.first_name}
                    onChange={(event) =>
                      setCustomerForm((current) => ({
                        ...current,
                        first_name: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="checkin-field">
                  <span>Last name</span>
                  <input
                    value={customerForm.last_name}
                    onChange={(event) =>
                      setCustomerForm((current) => ({
                        ...current,
                        last_name: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="checkin-field">
                  <span>Phone</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={customerForm.phone}
                    onChange={(event) =>
                      setCustomerForm((current) => ({
                        ...current,
                        phone: formatCustomerPhone(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="checkin-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={customerForm.email}
                    onChange={(event) =>
                      setCustomerForm((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  type="button"
                  className="checkin-action"
                  onClick={saveCustomer}
                  disabled={customerSaving}
                >
                  <AddIcon fontSize="small" />
                  {customerSaving ? "Saving..." : "Save Customer"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="checkin-section">
        <div className="checkin-section-title compact">
          <h2>Visit</h2>
        </div>

        <label className="checkin-field">
          <span>Miles</span>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={miles}
            onChange={(event) => setMiles(event.target.value)}
            placeholder="Current mileage"
          />
        </label>

        <div className="checkin-reasons" aria-label="Visit reasons">
          {reasonOptions.map((reason) => (
            <button
              type="button"
              key={reason.value}
              className={selectedReasons.includes(reason.value) ? "active" : ""}
              onClick={() => toggleReason(reason.value)}
            >
              {reason.label}
            </button>
          ))}
        </div>

        {selectedReasons.includes("Other") ? (
          <label className="checkin-field">
            <span>Other reason</span>
            <input
              value={otherReason}
              onChange={(event) => setOtherReason(event.target.value)}
              placeholder="Describe reason"
            />
          </label>
        ) : null}

        <label className="checkin-field">
          <span>Customer concern</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={4}
            placeholder="Noise, warning light, requested work..."
          />
        </label>
      </section>

      <footer className="checkin-footer">
        <button
          type="button"
          className="checkin-save"
          disabled={!canSave}
          onClick={saveQuote}
        >
          <SaveIcon fontSize="small" />
          {savingQuote ? "Saving..." : "Save Quote"}
        </button>
      </footer>
    </main>
  );
}

export default CheckIn;
